import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { ThemeProvider } from './ThemeContext';
import { AuthProvider, useAuth } from './AuthContext';
import { WorkflowProvider, useWorkflow } from './WorkflowContext';
import { HomeProvider } from './HomeContext';
import { NotesProvider } from './NotesContext';
import { RequestsProvider } from './RequestsContext';
import { PrepressProvider } from './PrepressContext';
import { useAccess } from './lib/access';
import AppShell from './components/AppShell';
import { Toast } from './components/Tag';
import Login from './pages/Login';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import Accounts from './pages/Accounts';
import Clients from './pages/Clients';
import ClientProfile from './pages/ClientProfile';
import Vendors from './pages/Vendors';
import Intake from './pages/Intake';
import RequestLog from './pages/RequestLog';
import TaskBoard from './pages/TaskBoard';
import Calendar from './pages/Calendar';
import CommandRoom from './pages/CommandRoom';
import Architecture from './pages/Architecture';
import AuditLog from './pages/AuditLog';
import Manual from './pages/Manual';
import Kpis from './pages/Kpis';
import Roadmap from './pages/Roadmap';
import Pipeline from './pages/Pipeline';
import QuoteEngine from './pages/QuoteEngine';
import QuoteBuilder from './pages/QuoteBuilder';
import CustomerCRM from './pages/CustomerCRM';
import MaterialsDies from './pages/MaterialsDies';
import QuoteRegistry from './pages/QuoteRegistry';
import FollowUps from './pages/FollowUps';
import CommsLog from './pages/CommsLog';
import Reorders from './pages/Reorders';
import SalesCalendar from './pages/SalesCalendar';
import SalesAI from './pages/SalesAI';
import WebLeads from './pages/WebLeads';
import Prepress from './pages/Prepress';
import OsCommand from './pages/os/OsCommand';
import PoInbox from './pages/os/PoInbox';
import PoReview from './pages/os/PoReview';
import OsSalesOrders from './pages/os/SalesOrders';
import SalesOrderPage from './pages/os/SalesOrderPage';
import OsTickets from './pages/os/Tickets';
import TicketPage from './pages/os/TicketPage';
import OsItems from './pages/os/Items';
import SpellingReview from './pages/SpellingReview';

function ToastHost() {
  const { toast } = useWorkflow();
  return <Toast toast={toast} />;
}

function RequireBackend({ children }: { children: ReactNode }) {
  const { canBackend } = useAccess();
  if (!canBackend) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function Gate() {
  const { user, ready, demo } = useAuth();
  if (!ready) return <div className="loading"><div className="spinner" /> Connecting…</div>;
  if (!user && !demo) return <Login />;
  const B = (el: ReactNode) => <RequireBackend>{el}</RequireBackend>;
  return (
    <HomeProvider>
      <WorkflowProvider>
        <NotesProvider>
          <RequestsProvider>
          <PrepressProvider>
          <AppShell>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/dashboard" element={B(<Dashboard />)} />
              <Route path="/clients" element={<Clients />} />
              <Route path="/client" element={<ClientProfile />} />
              <Route path="/vendors" element={<Vendors />} />
              <Route path="/accounts" element={<Accounts />} />
              <Route path="/intake" element={<Intake />} />
              <Route path="/requests" element={<RequestLog />} />
              <Route path="/tasks" element={<TaskBoard />} />
              <Route path="/prepress" element={<Prepress />} />
              <Route path="/calendar" element={<Calendar />} />
              <Route path="/pipeline" element={<Pipeline />} />
              <Route path="/quote-engine" element={<QuoteEngine />} />
              <Route path="/quote-builder" element={<QuoteBuilder />} />
              <Route path="/customers" element={<CustomerCRM />} />
              <Route path="/aliases" element={<SpellingReview />} />
              <Route path="/catalog" element={<MaterialsDies />} />
              <Route path="/quotes" element={<QuoteRegistry />} />
              <Route path="/follow-ups" element={<FollowUps />} />
              <Route path="/comms-log" element={<CommsLog />} />
              <Route path="/reorders" element={<Reorders />} />
              <Route path="/sales-calendar" element={<SalesCalendar />} />
              <Route path="/sales-ai" element={<SalesAI />} />
              <Route path="/web-leads" element={<WebLeads />} />
              <Route path="/os" element={<OsCommand />} />
              <Route path="/os/pos" element={<PoInbox />} />
              <Route path="/os/po" element={<PoReview />} />
              <Route path="/os/sos" element={<OsSalesOrders />} />
              <Route path="/os/so" element={<SalesOrderPage />} />
              <Route path="/os/tickets" element={<OsTickets />} />
              <Route path="/os/ticket" element={<TicketPage />} />
              <Route path="/os/items" element={<OsItems />} />
              <Route path="/ai" element={B(<CommandRoom />)} />
              <Route path="/architecture" element={B(<Architecture />)} />
              <Route path="/audit" element={B(<AuditLog />)} />
              <Route path="/manual" element={B(<Manual />)} />
              <Route path="/kpis" element={B(<Kpis />)} />
              <Route path="/roadmap" element={B(<Roadmap />)} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AppShell>
          <ToastHost />
          </PrepressProvider>
          </RequestsProvider>
        </NotesProvider>
      </WorkflowProvider>
    </HomeProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <Gate />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
